import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useEffect } from 'react';
import { queryKeys } from '~/shared/api/queryKeys';
import {
    type PayoutAdminOrderV1ApproveOrderRequest,
    type AdminPayoutOrderServiceListOrdersParams,
    type PayoutAdminOrderV1DeclineOrderRequest,
    type AdminPayoutOrderServiceGetOrderCascadeEventsParams,
    type AdminPayoutOrderServiceGetOrderEventsParams,
    type AdminPayoutOrderServiceGetOrderChecksParams,
    type PayoutAdminOrderV1InvestigateOrderRequest,
} from '~/generated/payoutBackend/model';
import {
    adminPayoutOrderServiceApproveOrder,
    adminPayoutOrderServiceDeclineOrder,
    adminPayoutOrderServiceGetOrderCascadeEvents,
    adminPayoutOrderServiceGetOrderChecks,
    adminPayoutOrderServiceGetOrderEvents,
    adminPayoutOrderServiceInvestigateOrder,
    adminPayoutOrderServiceListOrders,
    getAdminPayoutOrderServiceGetOrderCascadeEventsUrl,
    getAdminPayoutOrderServiceGetOrderChecksUrl,
    getAdminPayoutOrderServiceGetOrderEventsUrl,
    getAdminPayoutOrderServiceListOrdersUrl,
} from '~/generated/payoutBackend/order';
import { type Filters } from '../model';

type OrdersParams = Omit<
    AdminPayoutOrderServiceListOrdersParams,
    'filter.currency' | 'filter.publicStatuses' | 'filter.statuses'
> & {
    'filter.currency'?: Filters['currency'];
    'filter.publicStatuses'?: Filters['publicStatuses'];
    'filter.statuses'?: Filters['statuses'];
};

const toGeneratedOrdersParams = (params?: OrdersParams): AdminPayoutOrderServiceListOrdersParams | undefined => {
    return params as AdminPayoutOrderServiceListOrdersParams | undefined;
};

type QueryOptions<Data> = Omit<UseQueryOptions<Data, Error, Data>, 'queryFn' | 'queryKey'>;
type OrdersResponse = Awaited<ReturnType<typeof adminPayoutOrderServiceListOrders>>;
type OrderCascadeEventsResponse = Awaited<ReturnType<typeof adminPayoutOrderServiceGetOrderCascadeEvents>>;

export const useOrdersList = (
    params?: OrdersParams,
    refetchInterval?: number | false,
    options: QueryOptions<OrdersResponse> = {},
) => {
    const generatedParams = toGeneratedOrdersParams(params);

    return useQuery<OrdersResponse>({
        refetchInterval,
        refetchOnWindowFocus: true,
        queryFn: () => adminPayoutOrderServiceListOrders(generatedParams),
        queryKey: [queryKeys.PAYOUT_ORDERS, getAdminPayoutOrderServiceListOrdersUrl(generatedParams)],
        ...options,
    });
};

interface UseOrderMutationProps {
    onError?: (error: Error) => void;
    onSuccess?: () => void;
}

const invalidateOrderQueries = (queryClient: ReturnType<typeof useQueryClient>) => {
    return Promise.all([
        queryClient.invalidateQueries({
            queryKey: [queryKeys.PAYOUT_ORDERS],
        }),
        queryClient.invalidateQueries({
            queryKey: [queryKeys.PAYOUT_ORDER],
        }),
    ]);
};

export const useApproveOrder = (options?: UseOrderMutationProps) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload: PayoutAdminOrderV1ApproveOrderRequest) => {
            return await adminPayoutOrderServiceApproveOrder(payload as PayoutAdminOrderV1ApproveOrderRequest);
        },
        onError: options?.onError,
        onSuccess: () => {
            invalidateOrderQueries(queryClient).then(() => {
                options?.onSuccess?.();
                notifications.show({
                    color: 'green',
                    title: 'Уведомление',
                    message: 'Ордер подтвержден',
                });
            });
        },
    });
};

export const useDeclineOrder = (options?: UseOrderMutationProps) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload: PayoutAdminOrderV1DeclineOrderRequest) => {
            return await adminPayoutOrderServiceDeclineOrder(payload);
        },
        onError: options?.onError,
        onSuccess: () => {
            invalidateOrderQueries(queryClient).then(() => {
                options?.onSuccess?.();
                notifications.show({
                    color: 'green',
                    title: 'Уведомление',
                    message: 'Ордер отклонен',
                });
            });
        },
    });
};

export const useInvestigateOrder = (options?: UseOrderMutationProps) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload: PayoutAdminOrderV1InvestigateOrderRequest) => {
            return await adminPayoutOrderServiceInvestigateOrder(payload);
        },
        onError: options?.onError,
        onSuccess: () => {
            return Promise.all([
                queryClient.invalidateQueries({
                    queryKey: [queryKeys.PAYOUT_ORDERS],
                }),
                queryClient.invalidateQueries({
                    queryKey: [queryKeys.PAYOUT_ORDER],
                }),
            ]).then(() => {
                options?.onSuccess?.();
                notifications.show({
                    color: 'green',
                    title: 'Уведомление',
                    message: 'Ордер переведен в арбитраж',
                });
            });
        },
    });
};

export const useOrderEvents = (params: AdminPayoutOrderServiceGetOrderEventsParams) => {
    return useQuery({
        refetchOnWindowFocus: true,
        refetchOnMount: true,
        queryFn: () => adminPayoutOrderServiceGetOrderEvents(params),
        queryKey: [queryKeys.PAYOUT_ORDER, getAdminPayoutOrderServiceGetOrderEventsUrl(params)],
    });
};

export const useOrderCascadeEvents = (
    params: AdminPayoutOrderServiceGetOrderCascadeEventsParams,
    options: QueryOptions<OrderCascadeEventsResponse> = {},
) => {
    return useQuery<OrderCascadeEventsResponse>({
        refetchOnWindowFocus: true,
        refetchOnMount: true,
        queryFn: () => adminPayoutOrderServiceGetOrderCascadeEvents(params),
        queryKey: [queryKeys.PAYOUT_ORDER, getAdminPayoutOrderServiceGetOrderCascadeEventsUrl(params)],
        ...options,
    });
};

type OrderChecksResponse = Awaited<ReturnType<typeof adminPayoutOrderServiceGetOrderChecks>>;
type OrderChecksQueryOptions<Data> = QueryOptions<Data> & {
    onError?: VoidFunction;
};

export const useOrderChecks = (
    params: AdminPayoutOrderServiceGetOrderChecksParams,
    options: OrderChecksQueryOptions<OrderChecksResponse> = {},
) => {
    const { onError, ...queryOptions } = options;

    const queryResult = useQuery<OrderChecksResponse>({
        queryFn: () =>
            adminPayoutOrderServiceGetOrderChecks(params, {
                context: {
                    skipErrorNotification: true,
                },
            }),
        queryKey: [getAdminPayoutOrderServiceGetOrderChecksUrl(params)],
        ...queryOptions,
    });

    useEffect(() => {
        if (!queryResult.data) {
            onError?.();
        }
    }, [onError, queryResult.data]);

    return queryResult;
};
